import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";

import { alertActions } from "../../features/alert/alertSlice";
import { selectSelectedLeague } from "../../features/leaguemgmt/leagueManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import { assignPlayoffSeeds, resetPlayoffSeeds } from "../../lib/api";

const PlayoffButtonsModal = ({
    action,
    leagueId,
    showModal,
    setShowModal,
}: {
    action: "assign" | "reset";
    leagueId: number;
    showModal: boolean;
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const user = useAppSelector(state => state.auth.user);
    const league = useAppSelector(selectSelectedLeague);
    const dispatch = useAppDispatch();

    const handleAction = async (
        action: "assign" | "reset",
        leagueId: number,
    ) => {
        switch (action) {
            case "assign":
                if (
                    !league?.playoffBracket?.dates ||
                    league.playoffBracket.dates.championship.some(
                        d => d.date === null,
                    ) ||
                    (league.playoffBracket.dates.consolation &&
                        league.playoffBracket.dates.consolation.some(
                            d => d.date === null,
                        ))
                ) {
                    dispatch(
                        alertActions.error({
                            title: `No Playoff Dates Set!`,
                            text: `Playoff Dates must be set below for all rounds in order to begin playoff seeding`,
                            dismissible: true,
                        }),
                    );
                } else {
                    assignPlayoffSeeds(leagueId, user!.id);
                    dispatch(
                        alertActions.success({
                            title: `Playoff seeding has started`,
                            text: `Please wait up to 60 seconds for all seeds to be assigned before checking playoff seedings/schedule`,
                            dismissible: true,
                        }),
                    );
                }
                break;
            case "reset":
                resetPlayoffSeeds(leagueId, user!.id)
                    .then(() => {
                        dispatch(
                            alertActions.success({
                                title: `Playoff seeds reset`,
                                dismissible: true,
                            }),
                        );
                    })
                    .catch(e => {
                        dispatch(
                            alertActions.error({
                                title: `The was a problem resetting playoff seeds.`,
                                text: e,
                                dismissible: true,
                            }),
                        );
                    });
                break;
        }

        setShowModal(false);
    };

    const buildModalBody = () => {
        return action === "assign" ? (
            <>
                <p>
                    Are you sure you want to generate the playoff brackets for
                    this league and assign teams to their seeds?
                </p>
                <p>
                    This process can take up to a minute to generate and is
                    executed in the background.
                </p>
                <p>
                    You can continue to use the site while the bracket is
                    building.
                </p>
            </>
        ) : (
            <>
                <h5 className="text-danger text-center">
                    Are you sure you want to reset the playoff brackets for this
                    league?
                </h5>
                <h6 className="text-danger text-center">
                    This will remove all currently assigned seeds for playoff
                    games in this league!
                </h6>
            </>
        );
    };

    return (
        <Modal show={showModal} onHide={() => setShowModal(false)}>
            <Modal.Header>
                <Modal.Title>{`${
                    action.charAt(0).toUpperCase() + action.slice(1)
                } Playoff Seeds?`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{buildModalBody()}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowModal(false)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        handleAction(action, leagueId);
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PlayoffButtonsModal;
